/* Impersonation banner — sticky top bar shown when superadmin is "View as"
 * a specific tenant. Single button to exit and restore the original
 * superadmin session.
 *
 * The cloak: while this banner is showing, all audit events the superadmin
 * generates are routed to the PLATFORM audit chain (tenant_id=None) — the
 * tenant's own audit log stays clean. The platform admin still sees every
 * action in the CISO audit pack.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, LogOut, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ImpersonationBanner() {
    const { user, refresh } = useAuth();
    const [busy, setBusy] = useState(false);

    if (!user?.is_impersonating) return null;

    const exit = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/admin/automaton/impersonate/exit`);
            await refresh?.();
            toast.success("Impersonation ended");
            // Send the admin back to the tenants page rather than leaving them
            // mid-tenant-data they no longer own.
            window.location.href = "/admin";
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to exit impersonation");
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="impersonation-banner"
            role="status"
            aria-live="polite"
            className="relative z-[60] bg-gradient-to-r from-amber-500 via-amber-400 to-orange-400 text-amber-950 shadow-lg shadow-amber-500/30"
        >
            <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-2 flex items-center gap-3 flex-wrap text-[13px] font-medium">
                <Eye size={14} className="shrink-0" />
                <span className="font-mono-tr text-[10.5px] uppercase tracking-[0.18em] opacity-80 shrink-0">
                    Cloaked impersonation
                </span>
                <span className="shrink-0">·</span>
                <span className="truncate min-w-0 flex-1">
                    Viewing as{" "}
                    <strong className="font-semibold" data-testid="impersonation-target-slug">
                        {user.imp_target_slug}
                    </strong>
                    {user.imp_actor && (
                        <span className="opacity-80">
                            {" "}· you are <span className="font-mono-tr text-[12px]">{user.imp_actor}</span>
                        </span>
                    )}
                </span>
                <span className="hidden md:inline opacity-70 text-[11.5px]">
                    Their audit log won&rsquo;t see your visit · platform audit retains full trail
                </span>
                <button
                    onClick={exit}
                    disabled={busy}
                    data-testid="impersonation-exit"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-950 text-amber-50 hover:bg-amber-900 text-[12.5px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
                    Exit impersonation
                </button>
            </div>
        </div>
    );
}
